import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_SLOT_NAMES,
  cloneWavetablePreset,
  getWavetableCatalog,
  normalizeWavetablePreset,
  type FilterType,
  type InstrumentSlot,
  type WavetablePreset,
  type WtModRoute,
  type WtModSource,
  type WtModTarget,
  type WtWarpMode,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  WT_PRESET_CATEGORIES,
  wtCategorySortIndex,
} from "../lib/wavetableBank";
import { getLabActions } from "../labActions";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";
import { formatOctaveLabel, MusicalKeyboard } from "./MusicalKeyboard";
import { ParamKnob } from "./ParamKnob";
import { SearchableSelect, type SearchableSelectOption } from "./SearchableSelect";
import { Checkbox } from "./ui/Checkbox";
import { UnisonVoiceVisualizer } from "./docks/UnisonVoiceVisualizer";
import { instrumentPresetLearnPath } from "../controls/instrumentPresetLeaves";

const STORAGE_KEY = "audio-lab:wavetable-author-modal";
const PANEL_WIDTH = 1020;
const PANEL_MIN_HEIGHT = 680;

const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const WARP_MODES: WtWarpMode[] = ["none", "bend", "mirror", "sync"];
const LFO_WAVES = ["sine", "tri"] as const;
const SUB_WAVES = ["sine", "square"] as const;
const MOD_SOURCES: WtModSource[] = ["ampEnv", "modEnv", "lfo", "key"];
const MOD_TARGETS: WtModTarget[] = [
  "pitch",
  "cutoff",
  "amp",
  "tablePos1",
  "tablePos2",
  "warp1",
  "warp2",
  "unisonDetune",
  "unisonSpread",
  "unisonEdgeDecay",
  "pmDepth",
  "subLevel",
];

type PanelPos = { left: number; top: number };

type KnobSpec = {
  path: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
};

type SelectSpec = {
  path: string;
  label: string;
  options: readonly string[];
};

type SectionSpec = {
  title: string;
  /** Optional full-width control row above knobs (e.g. table picker). */
  tablePath?: "osc1.tableId" | "osc2.tableId";
  /** When true, render unison visualizer above knobs. */
  unisonViz?: boolean;
  /** Extra toggle row (sync). */
  syncToggle?: boolean;
  controls: Array<{ kind: "knob"; spec: KnobSpec } | { kind: "select"; spec: SelectSpec }>;
};

const SECTIONS: SectionSpec[] = [
  {
    title: "Osc 1",
    tablePath: "osc1.tableId",
    controls: [
      { kind: "knob", spec: { path: "osc1.position", label: "Pos", min: 0, max: 1, step: 0.001, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "osc1.warp", label: "Warp", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "select", spec: { path: "osc1.warpMode", label: "Mode", options: WARP_MODES } },
      { kind: "knob", spec: { path: "osc1.octave", label: "Oct", min: -3, max: 3, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.semis", label: "Semi", min: -12, max: 12, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.detuneCents", label: "Detune", min: -100, max: 100, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc1.level", label: "Level", min: 0, max: 1, step: 0.01, defaultValue: 0.75 } },
    ],
  },
  {
    title: "Osc 2",
    tablePath: "osc2.tableId",
    controls: [
      { kind: "knob", spec: { path: "osc2.position", label: "Pos", min: 0, max: 1, step: 0.001, defaultValue: 0.55 } },
      { kind: "knob", spec: { path: "osc2.warp", label: "Warp", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
      { kind: "select", spec: { path: "osc2.warpMode", label: "Mode", options: WARP_MODES } },
      { kind: "knob", spec: { path: "osc2.octave", label: "Oct", min: -3, max: 3, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.semis", label: "Semi", min: -12, max: 12, step: 1, defaultValue: 0 } },
      { kind: "knob", spec: { path: "osc2.detuneCents", label: "Detune", min: -100, max: 100, step: 1, defaultValue: 7 } },
      { kind: "knob", spec: { path: "osc2.level", label: "Level", min: 0, max: 1, step: 0.01, defaultValue: 0.55 } },
    ],
  },
  {
    title: "Unison",
    unisonViz: true,
    controls: [
      { kind: "knob", spec: { path: "voices", label: "Voices", min: 1, max: 9, step: 1, defaultValue: 5 } },
      { kind: "knob", spec: { path: "unisonDetune", label: "Detune", min: 0, max: 100, step: 0.5, defaultValue: 12 } },
      { kind: "knob", spec: { path: "unisonSpread", label: "Spread", min: 0, max: 1, step: 0.01, defaultValue: 0.65 } },
      { kind: "knob", spec: { path: "unisonEdgeDecay", label: "Edge", min: 0, max: 1, step: 0.01, defaultValue: 0.25 } },
      { kind: "knob", spec: { path: "glideMs", label: "Glide", min: 0, max: 500, step: 1, defaultValue: 0 } },
    ],
  },
  {
    title: "Mix / PM",
    syncToggle: true,
    controls: [
      { kind: "knob", spec: { path: "pmDepth", label: "PM", min: 0, max: 4, step: 0.01, defaultValue: 0 } },
      { kind: "select", spec: { path: "sub.wave", label: "Sub", options: SUB_WAVES } },
      { kind: "knob", spec: { path: "sub.octave", label: "Sub oct", min: -2, max: 0, step: 1, defaultValue: -1 } },
      { kind: "knob", spec: { path: "sub.level", label: "Sub lvl", min: 0, max: 1, step: 0.01, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "noise.level", label: "Noise", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
    ],
  },
  {
    title: "Filter",
    controls: [
      { kind: "select", spec: { path: "filter.type", label: "Type", options: FILTERS } },
      { kind: "knob", spec: { path: "filter.cutoffHz", label: "Cutoff", min: 40, max: 12000, step: 1, defaultValue: 4200 } },
      { kind: "knob", spec: { path: "filter.resonance", label: "Res", min: 0, max: 1, step: 0.01, defaultValue: 0.25 } },
    ],
  },
  {
    title: "Amp env",
    controls: [
      { kind: "knob", spec: { path: "ampEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.01 } },
      { kind: "knob", spec: { path: "ampEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "ampEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.7 } },
      { kind: "knob", spec: { path: "ampEnv.r", label: "R", min: 0.001, max: 3, step: 0.001, defaultValue: 0.35 } },
    ],
  },
  {
    title: "Mod env",
    controls: [
      { kind: "knob", spec: { path: "modEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.02 } },
      { kind: "knob", spec: { path: "modEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.35 } },
      { kind: "knob", spec: { path: "modEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.4 } },
      { kind: "knob", spec: { path: "modEnv.r", label: "R", min: 0.001, max: 3, step: 0.001, defaultValue: 0.4 } },
    ],
  },
  {
    title: "LFO",
    controls: [
      { kind: "select", spec: { path: "lfo.wave", label: "Wave", options: LFO_WAVES } },
      { kind: "knob", spec: { path: "lfo.rateHz", label: "Rate", min: 0.01, max: 20, step: 0.01, defaultValue: 0.35 } },
      { kind: "knob", spec: { path: "lfo.depth", label: "Depth", min: 0, max: 1, step: 0.01, defaultValue: 0.15 } },
    ],
  },
];

/**
 * Floating wavetable author: browser, knobs, unison viz, mod matrix, keyboard.
 */
export function WavetableAuthorModal() {
  const open = useStoreSelector(uiStore, s => s.authorOpen);
  const instrumentSlot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const bank = useStoreSelector(projectStore, s => s.wavetableBank);

  const slot = (instrumentSlot ?? 0) as InstrumentSlot;
  const instrument = instruments[slot];
  const isWavetable = instrument?.kind === "wavetable";
  const preset = isWavetable ? normalizeWavetablePreset(instrument.preset) : null;

  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [octaveShift, setOctaveShift] = useState(0);
  const [dragging, setDragging] = useState(false);

  const catalog = useMemo(() => getWavetableCatalog(), []);
  const tableOptions = useMemo((): SearchableSelectOption[] => {
    return catalog.map(entry => ({
      value: entry.id,
      label: entry.name,
      group: entry.group ?? entry.pack ?? entry.source,
    }));
  }, [catalog]);

  const bankId =
    bank.find(entry => entry.name === preset?.name)?.id
    ?? bank[0]?.id
    ?? "";

  const bankOptions = useMemo((): SearchableSelectOption[] => {
    const ordered = bank.slice().sort((a, b) => {
      const byCat = wtCategorySortIndex(a.category) - wtCategorySortIndex(b.category);
      if (byCat !== 0) {
        return byCat;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered.map(item => ({
      value: item.id,
      label: item.name,
      group: item.category,
      hint: item.tags?.join(", "),
    }));
  }, [bank]);

  const categoryFilters = useMemo(
    () => WT_PRESET_CATEGORIES.map(entry => ({ id: entry.id, label: entry.label })),
    [],
  );

  useEffect(() => {
    if (!open || !isWavetable) {
      return;
    }
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
    panelRef.current?.focus();
  }, [open, isWavetable]);

  useEffect(() => {
    if (!open || !isWavetable) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open, isWavetable]);

  useLayoutEffect(() => {
    if (!open || !isWavetable) {
      return;
    }
    setOctaveShift(0);
  }, [open, slot, isWavetable]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    getLabActions()?.closeInstrumentAuthor?.();
    patchUi({ authorOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function commit(next: WavetablePreset): void {
    getLabActions()?.updateInstrumentWavetablePreset?.(slot, next);
  }

  function patchPath(path: string, value: unknown): void {
    if (!preset) {
      return;
    }
    const next = cloneWavetablePreset(preset);
    setPath(next as unknown as Record<string, unknown>, path, value);
    if (path === "glideMs" && typeof value === "number" && value <= 0) {
      delete next.glideMs;
    }
    commit(normalizeWavetablePreset(next));
  }

  function updateRoutes(routes: WtModRoute[]): void {
    if (!preset) {
      return;
    }
    commit(normalizeWavetablePreset({ ...cloneWavetablePreset(preset), routes }));
  }

  if (!open || !isWavetable) {
    return null;
  }

  const slotLabel = DEFAULT_SLOT_NAMES[slot] ?? `I${slot + 1}`;
  const title = preset
    ? `Wavetable author · ${preset.name} · ${slotLabel}`
    : "Wavetable author";

  return createPortal(
    <div
      ref={panelRef}
      className={`synth-author fixed z-[55] flex h-[min(780px,calc(100vh-24px))] w-[min(1020px,calc(100vw-24px))] max-[720px]:h-[calc(100vh-16px)] max-[720px]:w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-xs text-slate-300 shadow-[0_28px_64px_rgba(0,0,0,0.5)] outline-none ${dragging ? "select-none" : ""}`}
      tabIndex={0}
      role="dialog"
      aria-modal="false"
      aria-label="Wavetable author"
      style={{ left: pos.left, top: pos.top }}
    >
      <div
        className="synth-author-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-white/8 bg-lab-panel-bar px-[0.7rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="synth-author-title min-w-0 truncate text-xs font-semibold text-slate-100">
          {title}
        </span>
        <button
          type="button"
          className="rounded border border-transparent bg-transparent px-2 py-0.5 text-xs text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-slate-200"
          onPointerDown={event => event.stopPropagation()}
          onClick={() => {
            close();
            getLabActions()?.setStatus?.("Wavetable author closed.");
          }}
        >
          Close
        </button>
      </div>

      <div className="synth-author-browser flex shrink-0 items-center gap-[0.45rem] border-b border-white/[0.06] px-[0.55rem] py-[0.45rem]">
        <SearchableSelect
          className="min-w-0 flex-1"
          ariaLabel="Wavetable preset browser"
          searchPlaceholder="Search name, category, or tag"
          emptyMessage="No presets match."
          value={bankId}
          options={bankOptions}
          filters={categoryFilters}
          onChange={id => getLabActions()?.applyWavetableBankEntry?.(slot, id)}
        />
        <div className="flex shrink-0 gap-[0.3rem]">
          <button
            type="button"
            className="border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px]"
            onClick={() => getLabActions()?.saveWavetableBank?.(slot)}
          >
            Save
          </button>
          <button
            type="button"
            className="border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px]"
            onClick={() => getLabActions()?.saveWavetableBankAs?.(slot)}
          >
            Save as…
          </button>
        </div>
      </div>

      {preset ? (
        <div className="synth-author-scroll flex min-h-0 flex-1 flex-col gap-[0.65rem] overflow-auto bg-[radial-gradient(120%_80%_at_50%_0%,rgba(61,139,253,0.05),transparent_55%),var(--panel)] px-[0.65rem] pt-[0.55rem] pb-3">
          <label className="flex items-center gap-2 text-[11px] tracking-[0.04em] text-slate-500 uppercase">
            <span>Name</span>
            <input
              type="text"
              className="min-w-0 flex-1 rounded-md border border-white/12 bg-white/[0.04] px-2 py-[0.35rem] text-xs tracking-normal text-slate-200 normal-case [font:inherit]"
              value={preset.name}
              onChange={event => patchPath("name", event.target.value)}
            />
          </label>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-[0.55rem]">
            {SECTIONS.map(section => (
              <section
                key={section.title}
                className="synth-author-section rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]"
              >
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  {section.title}
                </h3>
                {section.tablePath ? (
                  <div className="mb-[0.55rem]">
                    <SearchableSelect
                      className="w-full"
                      ariaLabel={`${section.title} table`}
                      searchPlaceholder="Search wavetable"
                      emptyMessage="No tables match."
                      value={String(getPath(preset, section.tablePath) ?? "")}
                      options={tableOptions}
                      onChange={id => patchPath(section.tablePath!, id)}
                    />
                  </div>
                ) : null}
                {section.unisonViz ? (
                  <UnisonVoiceVisualizer
                    voices={preset.voices}
                    detune={preset.unisonDetune}
                    spread={preset.unisonSpread}
                    edgeDecay={preset.unisonEdgeDecay}
                  />
                ) : null}
                {section.syncToggle ? (
                  <label className="mb-[0.55rem] flex items-center gap-2 text-[11px] text-lab-muted">
                    <Checkbox
                      checked={preset.sync}
                      aria-label="Sync osc2 to osc1"
                      onCheckedChange={sync => patchPath("sync", sync)}
                    />
                    Sync osc2 → osc1
                  </label>
                ) : null}
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  {section.controls.map(control =>
                    control.kind === "knob" ? (
                      <AuthorKnob
                        key={control.spec.path}
                        slot={slot}
                        spec={control.spec}
                        value={numberAtPath(preset, control.spec.path, control.spec.defaultValue)}
                        onChange={v => patchPath(control.spec.path, v)}
                      />
                    ) : (
                      <AuthorSelect
                        key={control.spec.path}
                        spec={control.spec}
                        value={String(
                          getPath(preset, control.spec.path)
                            ?? control.spec.options[0]
                            ?? "",
                        )}
                        onChange={v => patchPath(control.spec.path, v)}
                      />
                    ),
                  )}
                </div>
              </section>
            ))}
          </div>

          <AuthorRoutes slot={slot} routes={preset.routes} onChange={updateRoutes} />
        </div>
      ) : (
        <div className="synth-author-scroll muted flex min-h-0 flex-1 flex-col overflow-auto px-[0.65rem] pt-[0.55rem] pb-3">
          No wavetable preset for this slot.
        </div>
      )}

      <div className="synth-author-keys shrink-0 border-t border-white/8 bg-lab-panel-bar px-[0.55rem] pt-1.5 pb-2">
        <div className="mb-[0.3rem] flex items-center justify-between gap-2 text-[11px] text-lab-muted">
          <span>Keyboard</span>
          <span className="muted">{formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={48}
          octaves={3}
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
        <p className="muted mt-[0.35rem] mb-0 text-[11px]">
          A–' white · W E T Y U O P black · Z/X octave
        </p>
      </div>
    </div>,
    document.body,
  );
}

function AuthorKnob({
  slot,
  spec,
  value,
  onChange,
}: {
  slot: InstrumentSlot;
  spec: KnobSpec;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <ParamKnob
      label={spec.label}
      min={spec.min}
      max={spec.max}
      step={spec.step}
      value={value}
      defaultValue={spec.defaultValue}
      density="compact"
      size={44}
      format={v => formatPathValue(spec.path, v)}
      learnPath={instrumentPresetLearnPath(slot, spec.path)}
      onChange={onChange}
    />
  );
}

function AuthorSelect({
  spec,
  value,
  onChange,
}: {
  spec: SelectSpec;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-[4.2rem] flex-col items-center gap-[0.28rem]">
      <span className="text-center text-[10px] leading-tight tracking-[0.04em] text-slate-500 uppercase">
        {spec.label}
      </span>
      <select
        className="h-8 w-[4.6rem] rounded-md border border-white/12 bg-white/[0.04] px-1 text-[11px] text-slate-200 [font:inherit]"
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        {spec.options.map(opt => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function AuthorRoutes({
  slot,
  routes,
  onChange,
}: {
  slot: InstrumentSlot;
  routes: WtModRoute[];
  onChange: (routes: WtModRoute[]) => void;
}) {
  return (
    <div className="synth-author-routes rounded-lg border border-white/[0.07] bg-black/14 px-[0.55rem] pt-[0.45rem] pb-[0.55rem]">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
          Mod matrix
        </h3>
        <button
          type="button"
          className="px-[0.45rem] py-0.5 text-[11px]"
          onClick={() =>
            onChange([...routes, { source: "modEnv", target: "cutoff", depth: 0.45 }])
          }
        >
          + Route
        </button>
      </div>
      <div className="flex flex-col gap-[0.3rem]">
        {routes.map((route, index) => (
          <div key={index} className="flex flex-wrap items-center gap-[0.35rem]">
            <select
              className={authorRouteControl}
              value={route.source}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, source: event.target.value as WtModSource } : r,
                );
                onChange(next);
              }}
            >
              {MOD_SOURCES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              className={authorRouteControl}
              value={route.target}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, target: event.target.value as WtModTarget } : r,
                );
                onChange(next);
              }}
            >
              {MOD_TARGETS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ParamKnob
              min={-2}
              max={2}
              step={0.01}
              value={route.depth}
              defaultValue={0.45}
              density="compact"
              size={28}
              valuePlacement="end"
              ariaLabel={`Route ${index + 1} depth`}
              learnPath={instrumentPresetLearnPath(slot, `routes.${index}.depth`)}
              onChange={depth => {
                const next = routes.map((r, i) => (i === index ? { ...r, depth } : r));
                onChange(next);
              }}
            />
            <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
              <Checkbox
                checked={Boolean(route.invert)}
                aria-label={`Route ${index + 1} invert`}
                onCheckedChange={invert => {
                  const next = routes.map((r, i) =>
                    i === index ? { ...r, invert: invert || undefined } : r,
                  );
                  onChange(next);
                }}
              />
              inv
            </label>
            <button
              type="button"
              title="Remove route"
              onClick={() => onChange(routes.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const authorRouteControl =
  "h-7 rounded border border-white/12 bg-white/[0.04] px-[0.3rem] text-[11px] text-slate-200 [font:inherit]";

function getPath(preset: WavetablePreset, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = preset;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== "object") {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

function numberAtPath(preset: WavetablePreset, path: string, fallback: number): number {
  let value = getPath(preset, path);
  if (path === "glideMs" && value === undefined) {
    value = 0;
  }
  return typeof value === "number" ? value : fallback;
}

function formatPathValue(path: string, value: number): string {
  if (
    path.includes("Hz")
    || path === "glideMs"
    || path.includes("detune")
    || path.includes("octave")
    || path.includes("semis")
    || path === "voices"
  ) {
    return String(Math.round(value * 1000) / 1000);
  }
  if (path.includes("Env") || path.endsWith(".a") || path.endsWith(".d") || path.endsWith(".r")) {
    return value.toFixed(3);
  }
  return value.toFixed(2);
}

function loadPos(): PanelPos | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return undefined;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  const top = Math.max(12, Math.min(48, window.innerHeight * 0.06));
  return { left, top };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(
    8,
    window.innerHeight - Math.min(height, window.innerHeight - 16) - 8,
  );
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}
